/**
 * Site Config Composable - Shared
 *
 * Laedt die Runtime-Konfiguration eines Projekts vom Server
 * (GET <publicBase>/site-config) und wendet sie generisch an:
 *  - Theme-Farben als CSS-Variablen (kompatibel zu useTheme)
 *  - SEO (Titel, Description, og:image, noIndex)
 *  - Branding (Logo/Hero/Favicon) und Texte als reaktive Daten
 *
 * Damit koennen Template-basierte Projekte (z.B. Klone von projects/mavi)
 * ohne Rebuild ueber das Portal umkonfiguriert werden. Das Composable ist
 * bewusst projekt-agnostisch: Welches Projekt geliefert wird, entscheidet
 * der Server ueber Host/Subdomain/Demo-Slug — nicht der Client.
 *
 * Es werden NUR gesetzte Werte angewendet. Bestehende Projekte ohne
 * Runtime-Settings verhalten sich exakt wie bisher (Compile-Time-Config).
 */
import { ref, computed } from 'vue';
import { applyProjectFavicon } from '../utils/projectFavicon';
import { readPrerenderSiteConfigFromDocument } from '../utils/prerenderSiteConfigHydration';
import { ensureLogoLcpHints } from '../utils/lcpResourceHints';
// Modulweiter State: ein Site-Config pro App-Instanz reicht.
const siteConfig = ref(null);
const isLoading = ref(false);
const loadError = ref(null);
let prerenderSiteConfigHydrated = false;
const applySiteConfig = (config) => {
    siteConfig.value = config;
    applyTheme(config.theme || {});
    const logoUrl = config.branding?.logoUrl?.trim();
    if (logoUrl) {
        ensureLogoLcpHints(logoUrl);
    }
    try {
        applyProjectFavicon({
            faviconUrl: config.branding?.faviconUrl,
            projectName: config.project?.name,
            primaryColor: config.theme?.primaryColor
        });
    }
    catch (faviconError) {
        console.warn('Favicon apply failed, using defaults:', faviconError);
    }
};
/**
 * Liest Site-Config aus dem Prerender-HTML (synchron, ohne Netzwerk).
 * Wird in main.ts vor app.mount() aufgerufen, damit Logo/Theme sofort da sind.
 */
export function hydrateSiteConfigFromPrerenderDocument() {
    if (prerenderSiteConfigHydrated || siteConfig.value) {
        return Boolean(siteConfig.value);
    }
    const config = readPrerenderSiteConfigFromDocument();
    if (!config)
        return false;
    applySiteConfig(config);
    prerenderSiteConfigHydrated = true;
    return true;
}
/**
 * Basis-Pfad der SPA zur Laufzeit.
 *
 * Oeffentliche Demo-Vorschau (tasti-o.de/demo/<slug>): Router-Basis mit Slug-Prefix.
 * demo.tasti-o.de/<slug> ist Portal — dort immer '/'.
 */
export function getRuntimeBasePath() {
    if (typeof window === 'undefined')
        return '/';
    const path = window.location.pathname || '/';
    const demoPathMatch = path.match(/^\/demo\/([a-z0-9-]+)/i);
    if (demoPathMatch?.[1]) {
        return `/demo/${demoPathMatch[1].toLowerCase()}/`;
    }
    return '/';
}
/** True auf Demo-Vorschau (tasti-o.de/demo/<slug> oder demo.tasti-o.de/<slug>). */
export function isDemoHost() {
    if (typeof window === 'undefined')
        return false;
    return Boolean(getDemoSlugFromPath());
}
/** Demo-Slug aus der URL (tasti-o.de/demo/<slug> oder demo.tasti-o.de/<slug>). */
export function getDemoSlugFromPath() {
    if (typeof window === 'undefined')
        return '';
    const path = window.location.pathname || '/';
    const demoPathMatch = path.match(/^\/demo\/([a-z0-9-]+)/i);
    if (demoPathMatch?.[1]) {
        return demoPathMatch[1].toLowerCase();
    }
    const host = (window.location.hostname || '').toLowerCase().split(':')[0];
    if (host === 'demo.tasti-o.de' || host === 'www.demo.tasti-o.de') {
        const segment = path.split('/').filter(Boolean)[0] || '';
        if (segment && !['api', 'public', 'uploads', 'auth'].includes(segment.toLowerCase())) {
            return segment.toLowerCase();
        }
    }
    return '';
}
/** Wendet Theme-Farben als globale CSS-Variablen an (nur gesetzte Werte). */
const applyTheme = (theme) => {
    if (typeof document === 'undefined')
        return;
    const root = document.documentElement;
    const mapping = [
        ['--theme-primary', theme.primaryColor],
        ['--theme-secondary', theme.secondaryColor],
        ['--theme-accent', theme.accentColor],
        ['--theme-background', theme.backgroundColor],
        ['--theme-text-primary', theme.textColor],
        ['--theme-header-background', theme.headerBackgroundColor],
        ['--theme-header-text', theme.headerTextColor],
        ['--theme-border-radius', theme.borderRadius],
        ['--theme-font-family', theme.font]
    ];
    for (const [cssVar, value] of mapping) {
        if (value)
            root.style.setProperty(cssVar, String(value));
    }
};
/**
 * Laedt die Site-Config vom Server und wendet Theme/Favicon an.
 * Schlaegt der Request fehl, bleibt die kompilierte Konfiguration aktiv
 * (kein Breaking fuer Bestandsprojekte).
 */
export async function loadSiteConfig(publicApiBaseUrl) {
    if (typeof window === 'undefined')
        return null;
    hydrateSiteConfigFromPrerenderDocument();
    isLoading.value = true;
    loadError.value = null;
    let timeoutId = null;
    try {
        const base = (publicApiBaseUrl || '/public').replace(/\/$/, '');
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        if (controller) {
            timeoutId = window.setTimeout(() => controller.abort(), 15000);
        }
        const demoSlug = getDemoSlugFromPath();
        const response = await fetch(`${base}/site-config`, {
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                ...(demoSlug ? { 'X-Demo-Restaurant-Slug': demoSlug } : {}),
            },
            signal: controller?.signal
        });
        if (!response.ok) {
            loadError.value = `site-config request failed (${response.status})`;
            return null;
        }
        const payload = await response.json();
        const config = payload?.data || null;
        if (!config)
            return siteConfig.value;
        applySiteConfig(config);
        prerenderSiteConfigHydrated = true;
        return config;
    }
    catch (error) {
        loadError.value = error?.message || String(error);
        return siteConfig.value;
    }
    finally {
        if (timeoutId)
            window.clearTimeout(timeoutId);
        isLoading.value = false;
    }
}
/**
 * Reaktiver Zugriff auf das Runtime-Theme fuer useTheme().
 * Innerhalb eines computed aufgerufen ist der Zugriff reaktiv —
 * sobald die Site-Config geladen ist, aktualisieren sich alle Themes.
 */
export function getRuntimeSiteTheme() {
    return siteConfig.value?.theme || null;
}
/**
 * Reaktiver Zugriff auf die geladene Site-Config.
 * `load()` ist idempotent nutzbar (z.B. in main.ts nach dem Mount).
 */
export function useSiteConfig() {
    const content = computed(() => siteConfig.value?.content || null);
    const branding = computed(() => siteConfig.value?.branding || null);
    const contact = computed(() => siteConfig.value?.contact || null);
    const projectInfo = computed(() => siteConfig.value?.project || null);
    const ordering = computed(() => siteConfig.value?.ordering || null);
    return {
        siteConfig,
        isLoading,
        loadError,
        content,
        branding,
        contact,
        projectInfo,
        ordering,
        load: loadSiteConfig
    };
}
